import { Card, Spin, Tabs } from 'antd';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useResizeDetector } from 'react-resize-detector';

import Icon from '@ant-design/icons';
import { PageLoader } from '@common/components/PageLoader/PageLoader';
import { WATERFALL_ICON } from '@common/constants';
import { ShapeType, SideItemOption } from '@common/enums';
import { DrawingTabEnum } from '@common/enums/drawing-tab.enum';
import { EdgeModel } from '@common/models';
import { getProductDescription } from '@common/utils';
import ImageUtils from '@core/utils/ImageUtils';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedEdge } from '@ee-api/edges-api/hooks/useSelectedEdge';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { toggleTriggerZooming } from '@redux/slices/fabricSlice';
import { selectSelectedDrawingTab } from '@redux/slices/shapeSlice';

import { useProductsAsProductDetails } from '../../../context/useProductsAsProductDetails';
import FabricCanvas from './fabCanvas/fabricCanvas';
import ShapeTabs from './shapeTab/shapeTabs';
import { CanvasToolbar } from './toolbar/toolbar';
import AddShapeIcon from '/public/assets/icons/add_Shape.svg';
import DrawIcon from '/public/assets/icons/draw.svg';

import './design-countertop.scss';
import colors from './drawing-tabs/colors.module.scss';

export interface DesignCountertopProps {
    stepInfo: string;
}

interface WrappedCanvasProps {
    showDescription: boolean;
    showPreviewers: boolean;
}

const WrappedCanvas = ({ showDescription, showPreviewers }: WrappedCanvasProps) => {
    const { ref, width } = useResizeDetector();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    useEffect(() => {
        dispatch(toggleTriggerZooming());
    }, [dispatch, width]);

    const { isFetchingAnything } = useProductsAsProductDetails();
    const showLoader = isFetchingAnything;

    return (
        <div id="canvasWrapper" className="canvas-wrapper" ref={ref}>
            {showDescription ? (
                <div className="canvas-descriptor">
                    <div className="description">
                        {t('SCROLLABLE_AREA.COUNTERTOP.PREDEFINED_SHAPE_DESCRIPTION')}
                        <Icon
                            className="draw-icon"
                            component={() => (
                                <AddShapeIcon fill={colors.descriptionColor} stroke={colors.descriptionColor} />
                            )}
                        />
                        <br />
                        {t('SCROLLABLE_AREA.COUNTERTOP.FREE_DRAW_DESCRIPTION')}
                        <Icon
                            className="draw-icon"
                            component={() => (
                                <DrawIcon fill={colors.descriptionColor} stroke={colors.descriptionColor} />
                            )}
                        />
                    </div>
                </div>
            ) : null}
            {showLoader && <PageLoader />}
            <FabricCanvas width={width ?? 300} />
            {showPreviewers ? (
                <div className="previews">
                    <EdgePreviewer />
                    <WaterfallPreviewer />
                </div>
            ) : null}
        </div>
    );
};

interface DrawingAreaContentProps {
    shapesInDrawingArea: boolean;
    isFreeDrawingState: boolean;
    showToolbar: boolean;
    showShapeTab: boolean;
}

/**
 * Main drawing area consists of the canvas, the toolbars, and the shapetab(s)
 * @param shapesInDrawingArea whether there are any shapes
 * @param isFreeDrawingState are we in free draw state
 * @param showToolbar show/hide the toolbars
 * @param showShapeTab show/hide the shapeTabs
 */
const DrawingAreaContent = ({
    shapesInDrawingArea,
    isFreeDrawingState,
    showToolbar,
    showShapeTab,
}: DrawingAreaContentProps) => (
    <div className="drawing-area">
        <WrappedCanvas showDescription={!shapesInDrawingArea && !isFreeDrawingState} showPreviewers={showToolbar} />

        {showToolbar ? <CanvasToolbar /> : null}
        {showShapeTab ? <ShapeTabs /> : null}
    </div>
);

const DesignCountertop = ({ stepInfo }: DesignCountertopProps): JSX.Element => {
    const { t } = useTranslation();

    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);
    const { selectedValue: selectedColor } = useSelectedColor();

    const isFreeDrawingState = useAppSelector(selectSelectedDrawingTab) === DrawingTabEnum.FREE_DRAWING_TAB;

    return (
        <Card className={`design-countertop-card modal-mount-0`}>
            <Tabs
                items={[
                    {
                        label: (
                            <span className={'title'}>
                                {t('SCROLLABLE_AREA.COUNTERTOP.TITLE')} <StepInfo stepInfo={stepInfo} />
                            </span>
                        ),
                        key: 'design-countertop',
                        className: 'design-countertop-container',
                        disabled: !selectedColor,
                        children: (
                            <DrawingAreaContent
                                shapesInDrawingArea={!!areThereShapesInTheDrawingArea}
                                isFreeDrawingState={isFreeDrawingState}
                                showToolbar={!!selectedColor && !!areThereShapesInTheDrawingArea}
                                showShapeTab
                            />
                        ),
                    },
                ]}
            />
        </Card>
    );
};

const StepInfo = ({ stepInfo }: DesignCountertopProps) => {
    return stepInfo ? (
        <span className="step-counter"> ({stepInfo}) </span>
    ) : (
        <Spin style={{ fontSize: 24 }} className="loading-spinner" />
    );
};
const EdgePreviewer = () => {
    const { areThereShapesInTheDrawingArea, drawingShapes } = useContext(DrawingShapeContext);
    const { selectedValue: selectedEdge } = useSelectedEdge();

    if (selectedEdge && drawingShapes && areThereShapesInTheDrawingArea) {
        const appliedEdges = drawingShapes.reduce((edgeList, shape) => {
            if (
                !shape.edge ||
                shape.type === ShapeType.FAUX_SHAPE ||
                edgeList.some((edge: EdgeModel) => edge.productId === shape.edge?.productId)
            ) {
                return edgeList;
            }
            return [...edgeList, shape.edge];
        }, [] as EdgeModel[]);

        return (
            <div className="edge-previewer">
                {appliedEdges.map((edge: EdgeModel, index: number) => {
                    const description = getProductDescription(edge);

                    return (
                        <div key={index}>
                            <img alt="edge-previewer" src={ImageUtils.imgUrl(edge.imageUrl, 300)} />
                            <div className="edge-name" title={description}>
                                {description}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    } else {
        return null;
    }
};

const WaterfallPreviewer = () => {
    const { areThereShapesInTheDrawingArea, drawingShapes } = useContext(DrawingShapeContext);
    const { t } = useTranslation();

    const doesWaterfallExists: boolean = useMemo(() => {
        if (!drawingShapes || !areThereShapesInTheDrawingArea) {
            return false;
        }

        return drawingShapes.reduce(
            (exists, shape) =>
                exists || shape.drawingEdges.some((edge) => edge.sideItem?.type === SideItemOption.WATERFALL),
            false
        );
    }, [areThereShapesInTheDrawingArea, drawingShapes]);

    return (
        <>
            {doesWaterfallExists && (
                <div className="waterfall-previewer">
                    <img alt="waterfall-preview" src={WATERFALL_ICON} />
                    <div className="waterfall-name">{t('WATERFALL.TITLE')}</div>
                </div>
            )}
        </>
    );
};

export default DesignCountertop;
